<template>
  <div style="height: 100%" ref="mainContainer">
    <slot></slot>
    <div class="date-bar-container">
      <div class="d-flex">
        <v-btn-toggle v-model="dateOption" tile class="btn-toggle">
          <v-btn :value="dateOptions.custom">{{ $t('assets-date-option-custom') }}</v-btn>
        </v-btn-toggle>
        <v-btn-toggle
          class="btn-toggle"
          v-model="dateOption"
          v-if="dateOption !== dateOptions.custom"
          tile
        >
          <v-btn @click="onChangeDateOption(dateOptions.today)" :value="dateOptions.today">{{ $t('assets-date-option-today') }}</v-btn>
          <v-btn @click="onChangeDateOption(dateOptions.yesterday)" :value="dateOptions.yesterday">{{ $t('assets-date-option-yesterday') }}</v-btn>
          <v-btn @click="onChangeDateOption(dateOptions.thisWeek)" :value="dateOptions.thisWeek">{{ $t('assets-date-option-this-week') }}</v-btn>
          <v-btn @click="onChangeDateOption(dateOptions.lastWeek)" :value="dateOptions.lastWeek">{{ $t('assets-date-option-last-week') }}</v-btn>
          <v-btn @click="onChangeDateOption(dateOptions.lastSeen)" :value="dateOptions.lastSeen">{{ $t('last-seen') }}</v-btn>
        </v-btn-toggle>
        <v-btn v-if="dateOption !== dateOptions.custom" @click="onHistoryCancel" icon class="mt-1 ml-2 mr-1" color="primaryDark" small>
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <div class="d-flex" v-if="dateOption === dateOptions.custom">
          <VueCtkDateTimePicker
            id="indoor-history-start-date-picker"
            class="date-control"
            v-model="historyStartDate"
            :label="$t('assets-history-start-date')"
            no-header
            no-button
          />
          <VueCtkDateTimePicker
            id="indoor-history-end-date-picker"
            class="date-control"
            v-model="historyEndDate"
            :label="$t('assets-history-end-date')"
            no-header
            no-button
            no-shortcuts
          />
          <v-btn
            class="date-bar-btn"
            color="primary"
            depressed
            @click="onHistorySubmit"
            :disabled="invalidDates"
          >{{ $t('submit') }}</v-btn>
          <v-btn @click="onHistoryCancel" icon class="mt-1 ml-2 mr-1" color="primaryDark" small>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
      <v-row no-gutters align="center" justify="center">
        <v-col cols="12" sm="8">
          <div v-if="dateOption !== dateOptions.custom">
            <label class="date-display-label">
              {{ historyStartDateDisplayValue }} - {{ historyEndDateDisplayValue }}
            </label>
          </div>
        </v-col>
      </v-row>
      <div v-if="showHistoryNotAvailableMessage" class="no-events-message">
        {{ $t('assets-indoor-no-history-message') }}
      </div>
    </div>

    <!-- INDOOR EVENT LIST START -->
    <vue-draggable-resizable
      v-if="historyEventListData.length"
      :resizable="false"
      :parent="true"
      :z="1"
      :w="175"
      :h="'auto'"
      :max-height="400"
      :x="historyListXPos"
      :y="historyListYPos"
      :onDrag="onHistoryListDrag"
      class="event-list-wrapper"
    >
      <div class="event-list-container" ref="eventListContainer">
        <div class="active-floor-label" v-if="activeAreaId">
          <v-icon x-small>mdi-layers</v-icon>
          {{ activeFloorName }}
        </div>
        <div v-if="largeEventSet" class="event-summary">
          Showing {{ historyEventListData.length.toLocaleString() }} indoor events. Map dots are grouped by position.
        </div>
        <ul class="event-list">
          <li
            class="event-list-item"
            v-for="(event, index) in historyEventListData"
            :ref="'indoorHistoryEventIndex' + index"
            :key="index"
            :class="{
              selected: index === currentHistoryEventIndex,
              'other-floor': activeAreaId && event.areaId && event.areaId !== activeAreaId,
              unresolved: !event.positionResolved,
            }"
            @click="onSelectHistoryEvent(index)"
          >
            <div class="d-flex align-center">
              <span><span class="dot-icon" :class="dotIconClass(event)"></span></span>
              <span>{{ fmtEventTime(event.eventTime) }}</span>
            </div>
            <v-divider></v-divider>
          </li>
        </ul>
      </div>
    </vue-draggable-resizable>
    <!-- INDOOR EVENT LIST END -->

  </div>
</template>

<script>
import { mapActions } from 'vuex';
import VueCtkDateTimePicker from 'vue-ctk-date-time-picker';
import { scrollMixin } from '../../mixins/scrollParentToChild';
import { newLatLong } from '../../utils/calculateLatLng';
import mapboxgl from 'mapbox-gl';

const MAX_RANGE_DATE = 60;
const LARGE_EVENT_THRESHOLD = 500;
const SOURCE_LABELS = {
  EVENT_COORDINATES: 'Coordinates',
  CURRENT_BEACON_POSITION: 'Beacon coordinates',
  LOCATION_BEACON_MAP_POINT: 'Beacon position',
  UNRESOLVED: 'Map position unavailable',
};
const HISTORY_DOTS_SOURCE = 'indoor-history-dots-src-airfinder';
const HISTORY_DOTS_LAYER = 'indoor-history-dots-airfinder';

export default {
  components: {
    VueCtkDateTimePicker,
  },
  mixins: [scrollMixin],
  props: {
    tag: null,
    map: {
      default: null,
    },
    isAf3: {
      type: Boolean,
      default: false,
    },
    currentAreaId: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      dateOption: null,
      fetchingHistory: false,
      historyEventListData: [],
      currentHistoryEventIndex: -1,
      showHistoryNotAvailableMessage: false,
      historyStartDate: this.$moment().startOf('day'),
      historyEndDate: this.$moment(),
      dateOptions: {
        custom: '1',
        today: '2',
        yesterday: '3',
        thisWeek: '4',
        lastWeek: '5',
        lastSeen: '6',
      },
      historyListXPos: 0,
      historyListYPos: 0,
      activeAreaId: this.currentAreaId,
      floorDataCache: {},
      historyDotFeatures: [],
      historyPopup: null,
    };
  },
  mounted() {
    this.onChangeDateOption(this.dateOptions.lastSeen);
    this.dateOption = this.dateOptions.lastSeen;
    this.setDefaultHistoryListPosition();
    window.addEventListener('resize', this.setDefaultHistoryListPosition);
  },
  computed: {
    historyStartDateDisplayValue() {
      let d = typeof this.historyStartDate === 'string'
        ? this.$moment(this.historyStartDate, 'YYYY-MM-DD hh:mm a')
        : this.historyStartDate;
      return d.format('M/D/YYYY hh:mm A');
    },
    historyEndDateDisplayValue() {
      let d = typeof this.historyEndDate === 'string'
        ? this.$moment(this.historyEndDate, 'YYYY-MM-DD hh:mm a')
        : this.historyEndDate;
      return d.format('M/D/YYYY hh:mm A');
    },
    invalidDates() {
      return (
        this.fetchingHistory ||
        this.historyStartDate === null ||
        this.historyEndDate === null ||
        new Date(this.historyEndDate) < new Date(this.historyStartDate)
      );
    },
    activeFloorName() {
      const event = this.historyEventListData.find(e => e.areaId === this.activeAreaId);
      return (event && event.areaName) ? event.areaName : (this.activeAreaId || '');
    },
    largeEventSet() {
      return this.historyEventListData.length > LARGE_EVENT_THRESHOLD;
    },
  },
  methods: {
    ...mapActions('tag', ['getTagIndoorHistory']),
    ...mapActions('area', ['getAF3Points']),
    sourceLabel(src) {
      return SOURCE_LABELS[src] || src || '';
    },
    fmtEventTime(t) {
      return this.$moment(t).format('M/D/YYYY hh:mm A');
    },
    dotIconClass(event) {
      if (!event.positionResolved) return 'dot-unresolved';
      if (event.positionSource === 'LOCATION_BEACON_MAP_POINT') return 'dot-af2';
      return 'dot-af3';
    },
    async onHistorySubmit() {
      this.$emit('onSubmit');
      this.showHistoryNotAvailableMessage = false;
      this.historyEventListData = [];
      this.clearHistoryDots();

      if (!this.historyStartDate || !this.historyEndDate) return;

      const startDate = typeof this.historyStartDate === 'string'
        ? this.$moment(this.historyStartDate, 'YYYY-MM-DD hh:mm a')
        : this.historyStartDate;

      const endDate = typeof this.historyEndDate === 'string'
        ? this.$moment(this.historyEndDate, 'YYYY-MM-DD hh:mm a')
        : this.historyEndDate;

      const endDateMax = startDate.clone().add(MAX_RANGE_DATE, 'days');
      if (endDate.diff(endDateMax) > 0) {
        this.$toasted.show(this.$t('assets-date-range-message'), {
          position: 'bottom-right',
          className: ['toast-error'],
          duration: 5000,
        });
        return;
      }

      this.fetchingHistory = true;
      this.currentHistoryEventIndex = -1;

      try {
        const payload = {
          id: this.tag.id,
          startDate: startDate.format('YYYY-MM-DDTHH:mm:ss[Z]'),
          endDate: endDate.format('YYYY-MM-DDTHH:mm:ss[Z]'),
        };
        const response = await this.getTagIndoorHistory(payload);
        const events = (response && response.events) ? response.events : [];
        this.historyEventListData = events;
        this.showHistoryNotAvailableMessage = events.length === 0;
        this.fetchingHistory = false;

        if (events.length === 0) return;

        const initialAreaId = this.determineInitialArea(events);
        if (!initialAreaId) return;

        if (initialAreaId !== this.activeAreaId) {
          this.$emit('switch-area', initialAreaId);
        } else {
          await this.drawHistoryDots();
        }
      } catch (error) {
        this.showHistoryNotAvailableMessage = true;
        this.fetchingHistory = false;
      }
    },
    determineInitialArea(events) {
      const drawable = events.filter(e => {
        if (!e.areaId || !e.positionResolved) return false;
        if (e.positionSource === 'LOCATION_BEACON_MAP_POINT') {
          return e.mapPointX != null && e.mapPointY != null;
        }
        return e.x != null && e.y != null;
      });
      if (drawable.length) return drawable[0].areaId;
      const withArea = events.filter(e => e.areaId);
      return withArea.length ? withArea[0].areaId : null;
    },
    async onAreaSwitched(areaId) {
      this.activeAreaId = areaId;
      await this.drawHistoryDots();
    },
    async drawHistoryDots() {
      if (!this.map || !this.activeAreaId || !this.historyEventListData.length) return;

      this.clearHistoryDots();

      const features = [];

      // === AF3 path: x/y → newLatLong ===
      const af3Events = this.historyEventListData.filter(e =>
        e.areaId === this.activeAreaId &&
        e.positionResolved &&
        e.positionSource !== 'LOCATION_BEACON_MAP_POINT' &&
        e.x != null && e.y != null
      );

      if (af3Events.length) {
        const floorData = await this.getOrFetchFloorData(this.activeAreaId);
        if (floorData && floorData.distance && floorData.mapDistance) {
          const scale = (floorData.mapDistance * 1000) / floorData.distance;
          const referencePoint = [floorData.originPointLat, floorData.originPointLng];
          const posMap = new Map();
          af3Events.forEach(e => {
            const key = `${e.x}|${e.y}`;
            if (!posMap.has(key)) {
              posMap.set(key, {
                x: e.x, y: e.y, events: [],
                areaName: e.areaName || '', zoneName: e.zoneName || '',
                locationType: e.locationType || '', positionSource: e.positionSource || '',
              });
            }
            posMap.get(key).events.push(e);
          });
          posMap.forEach(group => {
            const coords = newLatLong(group.x / 10, group.y / 10, 0, scale, referencePoint);
            features.push({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: coords },
              properties: {
                count: group.events.length,
                firstTime: group.events[0].eventTime,
                lastTime: group.events[group.events.length - 1].eventTime,
                areaName: group.areaName, zoneName: group.zoneName,
                locationType: group.locationType, positionSource: group.positionSource,
                isMapPoint: false,
                x: group.x, y: group.y,
                mpx: null, mpy: null,
                locationBeaconName: '', locationMac: '', mapPoint: '',
              },
            });
          });
        }
      }

      // === AF2 mapPoint path: use [mapPointX, mapPointY] directly as GeoJSON coordinates ===
      // This mirrors assets-map-view.vue drawDevices() which does:
      //   coordinates: tag.locationMapPoint  (= mapPoint.split(',').map(Number) = [mapPointX, mapPointY])
      const mapPointEvents = this.historyEventListData.filter(e =>
        e.areaId === this.activeAreaId &&
        e.positionResolved &&
        e.positionSource === 'LOCATION_BEACON_MAP_POINT' &&
        typeof e.mapPointX === 'number' && typeof e.mapPointY === 'number'
      );

      if (mapPointEvents.length) {
        const posMap = new Map();
        mapPointEvents.forEach(e => {
          const key = `MP|${e.mapPointX}|${e.mapPointY}`;
          if (!posMap.has(key)) {
            posMap.set(key, {
              mapPointX: e.mapPointX, mapPointY: e.mapPointY, events: [],
              areaName: e.areaName || '', zoneName: e.zoneName || '',
              locationType: e.locationType || '', positionSource: e.positionSource || '',
              locationBeaconName: e.locationBeaconName || '',
              locationMac: e.locationMac || '', mapPoint: e.mapPoint || '',
            });
          }
          posMap.get(key).events.push(e);
        });
        posMap.forEach(group => {
          features.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [group.mapPointX, group.mapPointY] },
            properties: {
              count: group.events.length,
              firstTime: group.events[0].eventTime,
              lastTime: group.events[group.events.length - 1].eventTime,
              areaName: group.areaName, zoneName: group.zoneName,
              locationType: group.locationType, positionSource: group.positionSource,
              isMapPoint: true,
              x: null, y: null,
              mpx: group.mapPointX, mpy: group.mapPointY,
              locationBeaconName: group.locationBeaconName,
              locationMac: group.locationMac, mapPoint: group.mapPoint,
            },
          });
        });
      }

      if (!features.length) return;

      this.historyDotFeatures = features;
      const geojsonData = { type: 'FeatureCollection', features };

      if (this.map.getSource(HISTORY_DOTS_SOURCE)) {
        this.map.getSource(HISTORY_DOTS_SOURCE).setData(geojsonData);
      } else {
        this.map.addSource(HISTORY_DOTS_SOURCE, { type: 'geojson', data: geojsonData });

        this.map.addLayer({
          id: HISTORY_DOTS_LAYER,
          source: HISTORY_DOTS_SOURCE,
          type: 'circle',
          paint: {
            'circle-radius': 9,
            'circle-color': ['case', ['==', ['get', 'isMapPoint'], true], '#ffa726', '#faff67'],
            'circle-opacity': 0.9,
            'circle-stroke-width': ['case', ['>', ['get', 'count'], 1], 2.5, 1.5],
            'circle-stroke-color': [
              'case',
              ['all', ['==', ['get', 'isMapPoint'], true], ['>', ['get', 'count'], 1]], '#bf360c',
              ['==', ['get', 'isMapPoint'], true], '#e65100',
              ['>', ['get', 'count'], 1], '#1565c0',
              '#555'
            ],
          },
        });

        this.map.on('mouseenter', HISTORY_DOTS_LAYER, this.onDotMouseEnter);
        this.map.on('click', HISTORY_DOTS_LAYER, this.onDotMouseEnter);
        this.map.on('mouseleave', HISTORY_DOTS_LAYER, this.onDotMouseLeave);
      }
    },
    buildDotPopupHtml(props) {
      const isMapPoint = props.isMapPoint === true || props.isMapPoint === 'true';
      const header = isMapPoint ? 'Historical beacon position' : 'Historical coordinate position';
      const areaZone = [props.areaName, props.zoneName].filter(Boolean).join(' / ') || '--';
      const fmt = t => this.$moment(t).format('M/D/YYYY hh:mm A');
      const timeBlock = props.count > 1
        ? `<b>${props.count} events</b><br><b>First:</b> ${fmt(props.firstTime)}<br><b>Last:</b> ${fmt(props.lastTime)}`
        : `<b>Time:</b> ${fmt(props.firstTime)}`;
      const beaconRow = props.locationBeaconName ? `<br><b>Beacon:</b> ${props.locationBeaconName}` : '';
      const typeRow = props.locationType ? `<br><b>Type:</b> ${props.locationType}` : '';
      return `<div style="font-size:12px;line-height:1.7"><b>${header}</b><br>${timeBlock}<br><b>Area:</b> ${areaZone}${beaconRow}${typeRow}<br><b>Source:</b> ${this.sourceLabel(props.positionSource)}</div>`;
    },
    onDotMouseEnter(e) {
      if (!e.features || !e.features.length) return;
      this.map.getCanvas().style.cursor = 'pointer';
      const html = this.buildDotPopupHtml(e.features[0].properties);
      if (this.historyPopup) this.historyPopup.remove();
      this.historyPopup = new mapboxgl.Popup({ closeOnClick: true, maxWidth: '240px' })
        .setLngLat(e.features[0].geometry.coordinates.slice())
        .setHTML(html)
        .addTo(this.map);
    },
    onDotMouseLeave() {
      if (this.map) this.map.getCanvas().style.cursor = '';
    },
    clearHistoryDots() {
      if (!this.map) return;
      if (this.historyPopup) {
        this.historyPopup.remove();
        this.historyPopup = null;
      }
      this.historyDotFeatures = [];
      this.map.off('mouseenter', HISTORY_DOTS_LAYER, this.onDotMouseEnter);
      this.map.off('click', HISTORY_DOTS_LAYER, this.onDotMouseEnter);
      this.map.off('mouseleave', HISTORY_DOTS_LAYER, this.onDotMouseLeave);
      if (this.map.getLayer(HISTORY_DOTS_LAYER)) {
        this.map.removeLayer(HISTORY_DOTS_LAYER);
      }
      if (this.map.getSource(HISTORY_DOTS_SOURCE)) {
        this.map.removeSource(HISTORY_DOTS_SOURCE);
      }
    },
    async getOrFetchFloorData(areaId) {
      if (this.floorDataCache[areaId]) return this.floorDataCache[areaId];
      try {
        const data = await this.getAF3Points(areaId);
        if (data) {
          this.$set(this.floorDataCache, areaId, data);
          return data;
        }
      } catch (e) {
        // area has no AF3 reference points — dots not drawable
      }
      return null;
    },
    onHistoryCancel() {
      this.clearHistoryDots();
      this.$emit('onCancel');
    },
    async onSelectHistoryEvent(index) {
      this.currentHistoryEventIndex = index;
      if (this.$refs['indoorHistoryEventIndex' + index]) {
        scrollMixin.methods.scrollParentToChild(
          this.$refs.eventListContainer,
          this.$refs['indoorHistoryEventIndex' + index][0]
        );
      }
      const event = this.historyEventListData[index];
      if (!event) return;

      if (event.areaId && event.areaId !== this.activeAreaId) {
        this.$emit('switch-area', event.areaId);
        return;
      }

      const isMapPoint = event.positionSource === 'LOCATION_BEACON_MAP_POINT';
      const isDrawable = event.positionResolved && (
        (isMapPoint && typeof event.mapPointX === 'number' && typeof event.mapPointY === 'number') ||
        (!isMapPoint && event.x != null && event.y != null)
      );

      if (isDrawable && this.map && this.map.getLayer(HISTORY_DOTS_LAYER)) {
        const matchExpr = isMapPoint
          ? ['all', ['==', ['get', 'isMapPoint'], true], ['==', ['get', 'mpx'], event.mapPointX], ['==', ['get', 'mpy'], event.mapPointY]]
          : ['all', ['==', ['get', 'isMapPoint'], false], ['==', ['get', 'x'], event.x], ['==', ['get', 'y'], event.y]];
        this.map.setPaintProperty(HISTORY_DOTS_LAYER, 'circle-color', [
          'case', matchExpr, '#ff6b35',
          ['==', ['get', 'isMapPoint'], true], '#ffa726', '#faff67',
        ]);
        this.map.setPaintProperty(HISTORY_DOTS_LAYER, 'circle-radius', [
          'case', matchExpr, 12, 9,
        ]);
      }
    },
    onChangeDateOption(option) {
      if (option === this.dateOption) return;
      switch (option) {
        case this.dateOptions.today:
          this.historyStartDate = this.$moment().startOf('day');
          this.historyEndDate = this.$moment();
          break;
        case this.dateOptions.yesterday:
          this.historyStartDate = this.$moment().subtract(1, 'day').startOf('day');
          this.historyEndDate = this.$moment().subtract(1, 'day').endOf('day');
          break;
        case this.dateOptions.thisWeek:
          this.historyStartDate = this.$moment().startOf('week').add(1, 'day');
          this.historyEndDate = this.$moment();
          break;
        case this.dateOptions.lastWeek:
          this.historyStartDate = this.$moment().subtract(1, 'week').startOf('week').add(1, 'day');
          this.historyEndDate = this.$moment().subtract(1, 'week').endOf('week').add(1, 'day');
          break;
        case this.dateOptions.lastSeen: {
          const week = 7;
          this.historyStartDate = this.$moment()
            .subtract(this.tag.notSeenInDays + week * 2, 'day')
            .startOf('day');
          if (this.tag.notSeenInDays < MAX_RANGE_DATE - week * 2) {
            this.historyEndDate = this.$moment();
          } else {
            this.historyEndDate = this.$moment().subtract(this.tag.notSeenInDays, 'day').endOf('day');
          }
          break;
        }
        default:
          break;
      }
      this.onHistorySubmit();
    },
    setDefaultHistoryListPosition() {
      const mainWidth = this.$refs.mainContainer.clientWidth;
      const mainHeight = this.$refs.mainContainer.clientHeight;
      this.historyListXPos = Math.floor(mainWidth - mainWidth * 0.02 - 280);
      this.historyListYPos = Math.floor(mainHeight * 0.2);
    },
    onHistoryListDrag(x, y) {
      this.historyListXPos = x;
      this.historyListYPos = y;
    },
  },
  beforeDestroy() {
    this.clearHistoryDots();
    window.removeEventListener('resize', this.setDefaultHistoryListPosition);
  },
};
</script>

<style lang="scss" scoped>
.date-bar-container {
  position: absolute;
  right: 0;
  z-index: 3;
  margin: 5px 10px 10px 120px;
  border: 1px solid white;
  background-color: white;
  border-radius: 3px;

  .btn-toggle {
    margin: 5px;
  }
}

.date-control {
  padding: 5px;
  width: 220px;

  @media (min-width: 1280px) {
    width: 250px;
  }
}

::v-deep #indoor-history-start-date-picker-input,
::v-deep #indoor-history-end-date-picker-input {
  font-size: 12px;

  @media (min-width: 1280px) {
    font-size: 14px;
  }
}

.date-bar-btn {
  align-self: center;
  margin-right: 5px;
}

.date-display-label {
  margin: 10px 10px 10px 100px;
}

.event-list-wrapper {
  position: absolute;
}

.event-list-container {
  background-color: white;
  opacity: 0.8;
  padding: 10px 2px 10px 5px;
  z-index: 1;
  border: 1px solid;
  border-radius: 3px;
  text-align: left;
  overflow-y: auto;
  width: 175px;
  max-height: 350px;
  cursor: move;

  .event-summary {
    padding: 4px 8px;
    font-size: 10px;
    color: #555;
    background: #fff8e1;
    border-bottom: 1px solid #ffe082;
  }

  .event-list {
    list-style: none;
    padding: 0;

    .selected {
      background: radial-gradient(#faff67, transparent);
    }

    .unresolved {
      opacity: 0.6;
    }

    .other-floor {
      opacity: 0.42;
      background-color: #f5f5f5;
    }

    .event-list-item {
      padding: 5px;
      cursor: pointer;
      font-size: 11px;

      &:hover {
        background: radial-gradient(#76e9ff, transparent);
      }

      .dot-icon {
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        margin: 0 4px 0 2px;
        flex-shrink: 0;

        &.dot-af2 {
          background-color: #ffa726;
          border: 1.5px solid #e65100;
        }

        &.dot-af3 {
          background-color: #faff67;
          border: 1.5px solid #555;
        }

        &.dot-unresolved {
          background-color: #ccc;
          border: 1.5px solid #999;
        }
      }
    }
  }
}

.active-floor-label {
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 600;
  background: #e3f2fd;
  border-bottom: 1px solid #bbdefb;
  display: flex;
  align-items: center;
  gap: 4px;
}

.no-events-message {
  padding: 8px 10px 6px;
  font-size: 12px;
  color: #555;
  border-top: 1px solid #e0e0e0;
}
</style>
